'use client';

import { FunctionComponent, useState, useEffect } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useTranslate } from '@xFrame4/components/Hooks';
import ALink from '@xFrame4/components/next/ALink';
import { formatPrice } from '@xFrame4/common/Utils';
import { formatDateUserFriendly } from '@xFrame4/common/Functions';
import PaymentLink from '@business/payments/PaymentLink';
import { PaymentLinkStatus } from '@business/payments/generated/PaymentLink.generated';
import PaymentLinkCard from './PaymentLinkCard';
import PaymentDetailRow from './PaymentDetailRow';
import PaymentDetailsSection from './PaymentDetailsSection';
import PaymentContactSupport from './PaymentContactSupport';

export interface PaymentLinkConfirmPaymentProps
{
    uniqueLinkId: string;
    language: string;
}

/**
 * Component: Payment confirmation client component that checks payment status after returning from Netopia.
 */
const PaymentLinkConfirmPayment: FunctionComponent<PaymentLinkConfirmPaymentProps> = (props) =>
{
    const t = useTranslate();
    
    const [paymentLink, setPaymentLink] = useState<PaymentLink | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [checkCount, setCheckCount] = useState<number>(0);
    const [finalStatus, setFinalStatus] = useState<PaymentLinkStatus | null>(null);

    /** Load and check payment status */
    useEffect(() =>
    {
        checkPaymentStatus();
    }, [props.uniqueLinkId]);

    /** Set up polling to check payment status */
    useEffect(() =>
    {
        if (checkCount > 0 && !finalStatus)
        {
            const timer = setTimeout(() =>
            {
                checkPaymentStatus();
            }, 1000); // Check every second

            return () => clearTimeout(timer);
        }
    }, [checkCount, finalStatus]);

    /** Check the payment status */
    const checkPaymentStatus = async () =>
    {
        try
        {
            const link = await PaymentLink.getPaymentLinkByUniqueId(props.uniqueLinkId);
            
            if (link)
            {
                setPaymentLink(link);
                
                // Check if we have a final status
                if (link.status === PaymentLinkStatus.PAID || 
                    link.status === PaymentLinkStatus.PAYMENT_FAILED)
                {
                    setFinalStatus(link.status);
                    setIsLoading(false);
                }
                else
                {
                    // Continue checking
                    setCheckCount(checkCount + 1);
                }
            }
            else
            {
                setIsLoading(false);
            }
        }
        catch (error)
        {
            console.error('Error checking payment status:', error);
            setIsLoading(false);
        }
    };


    // Loading/Checking state
    const cmpChecking = (
        <div className="text-center py-5">
            <Spinner animation="border" role="status" className="mb-3">
                <span className="visually-hidden">{t('LOADING', props.language)}</span>
            </Spinner>
            <h3 className="h4">{t('CHECKING_PAYMENT_STATUS', props.language)}</h3>
            <p className="text-muted">{t('PLEASE_WAIT', props.language)}</p>
        </div>
    );

    // Payment successful
    const cmpPaymentSuccess = paymentLink && (
        <div className="payment-success text-center">
            <div className="success-icon mb-4">
                <i className="bi bi-check-circle-fill text-success display-1"></i>
            </div>
            
            <Alert variant="success" className="mb-4">
                <h3 className="h4 mb-3">{t('PAYMENT_SUCCESSFUL', props.language)}</h3>
                <p className="mb-0">{t('PAYMENT_SUCCESSFUL_MESSAGE', props.language)}</p>
            </Alert>

            <PaymentDetailsSection title={t('TRANSACTION_DETAILS', props.language)}>
                <PaymentDetailRow 
                    label={t('AMOUNT', props.language)} 
                    value={formatPrice(paymentLink.amount, true, paymentLink.currency)}
                />
                
                {paymentLink.paymentGatewayTransactionId && (
                    <PaymentDetailRow 
                        label={t('TRANSACTION_ID', props.language)} 
                        value={paymentLink.paymentGatewayTransactionId}
                    />
                )}
                
                {paymentLink.dateModified && (
                    <PaymentDetailRow 
                        label={t('PAYMENT_DATE', props.language)} 
                        value={formatDateUserFriendly(paymentLink.dateModified, props.language)}
                    />
                )}
                
                {paymentLink.description && (
                    <PaymentDetailRow 
                        label={t('DESCRIPTION', props.language)} 
                        value={paymentLink.description}
                        isMultiline={true}
                    />
                )}
            </PaymentDetailsSection>

            <PaymentDetailsSection title={t('CUSTOMER_DETAILS', props.language)}>
                {paymentLink.customerName && (
                    <PaymentDetailRow 
                        label={t('NAME', props.language)} 
                        value={paymentLink.customerName}
                    />
                )}
                
                {paymentLink.customerEmail && (
                    <PaymentDetailRow 
                        label={t('EMAIL', props.language)} 
                        value={paymentLink.customerEmail}
                    />
                )}
                
                {paymentLink.customerPhone && (
                    <PaymentDetailRow 
                        label={t('PHONE', props.language)} 
                        value={paymentLink.customerPhone}
                    />
                )}
            </PaymentDetailsSection>

            <div className="confirmation-message">
                <Alert variant="info">
                    <p className="mb-0">{t('PAYMENT_CONFIRMATION_EMAIL_SENT', props.language)}</p>
                </Alert>
            </div>

            <div className="action-buttons mt-4">
                <ALink 
                    href={`/${props.language}`}
                    className="btn btn-primary btn-lg px-5"
                >
                    {t('GO_TO_HOMEPAGE', props.language)}
                </ALink>
            </div>
        </div>
    );

    // Payment failed
    const cmpPaymentFailed = paymentLink && (
        <div className="payment-failed text-center">
            <div className="error-icon mb-4">
                <i className="bi bi-x-circle-fill text-danger display-1"></i>
            </div>
            
            <Alert variant="danger" className="mb-4">
                <h3 className="h4 mb-3">{t('PAYMENT_FAILED', props.language)}</h3>
                <p className="mb-2">{t('PAYMENT_FAILED_MESSAGE', props.language)}</p>
                {paymentLink.paymentGatewayMessage && (
                    <p className="mb-0">
                        <strong>{t('ERROR_MESSAGE', props.language)}:</strong> {paymentLink.paymentGatewayMessage}
                    </p>
                )}
            </Alert>

            <PaymentDetailsSection title={t('TRANSACTION_DETAILS', props.language)}>
                <PaymentDetailRow 
                    label={t('AMOUNT', props.language)} 
                    value={formatPrice(paymentLink.amount, true, paymentLink.currency)}
                />
                
                {paymentLink.paymentGatewayTransactionId && (
                    <PaymentDetailRow 
                        label={t('TRANSACTION_ID', props.language)} 
                        value={paymentLink.paymentGatewayTransactionId}
                    />
                )}
                
                {paymentLink.description && (
                    <PaymentDetailRow 
                        label={t('DESCRIPTION', props.language)} 
                        value={paymentLink.description}
                        isMultiline={true}
                    />
                )}
            </PaymentDetailsSection>

            <div className="action-buttons mt-4">
                <ALink 
                    href={`/${props.language}/payment-link/${paymentLink.uniqueLinkId}`}
                    className="btn btn-primary btn-lg px-5 me-3"
                >
                    {t('TRY_AGAIN', props.language)}
                </ALink>
                
                <ALink 
                    href={`/${props.language}`}
                    className="btn btn-secondary btn-lg px-5"
                >
                    {t('GO_TO_HOMEPAGE', props.language)}
                </ALink>
            </div>
        </div>
    );

    // Payment pending or timeout
    const cmpPaymentPending = paymentLink && (
        <div className="payment-pending text-center">
            <div className="warning-icon mb-4">
                <i className="bi bi-exclamation-triangle-fill text-warning display-1"></i>
            </div>
            
            <Alert variant="warning" className="mb-4">
                <h3 className="h4 mb-3">{t('PAYMENT_STATUS_UNKNOWN', props.language)}</h3>
                <p className="mb-0">{t('PAYMENT_STATUS_UNKNOWN_MESSAGE', props.language)}</p>
            </Alert>

            <PaymentDetailsSection title={t('TRANSACTION_DETAILS', props.language)}>
                <PaymentDetailRow 
                    label={t('STATUS', props.language)} 
                    value={
                        paymentLink.status === PaymentLinkStatus.PAYMENT_PENDING 
                            ? t('PAYMENT_STATUS_PENDING', props.language)
                            : t('PAYMENT_STATUS_CREATED', props.language)
                    }
                />
                
                <PaymentDetailRow 
                    label={t('AMOUNT', props.language)} 
                    value={formatPrice(paymentLink.amount, true, paymentLink.currency)}
                />
                
                {paymentLink.paymentGatewayTransactionId && (
                    <PaymentDetailRow 
                        label={t('TRANSACTION_ID', props.language)} 
                        value={paymentLink.paymentGatewayTransactionId}
                    />
                )}
            </PaymentDetailsSection>

            <Alert variant="info">
                <p className="mb-0">{t('PAYMENT_PENDING_INSTRUCTIONS', props.language)}</p>
            </Alert>

            <div className="action-buttons mt-4">
                <ALink 
                    href={`/${props.language}`}
                    className="btn btn-primary btn-lg px-5"
                >
                    {t('GO_TO_HOMEPAGE', props.language)}
                </ALink>
            </div>
        </div>
    );

    // No payment link found
    const cmpNotFound = (
        <div className="text-center">
            <Alert variant="danger">
                <h3 className="h4">{t('PAYMENT_LINK_NOT_FOUND', props.language)}</h3>
                <p>{t('PAYMENT_LINK_NOT_FOUND_MESSAGE', props.language)}</p>
            </Alert>
            
            <div className="action-buttons mt-4">
                <ALink 
                    href={`/${props.language}`}
                    className="btn btn-primary btn-lg px-5"
                >
                    {t('GO_TO_HOMEPAGE', props.language)}
                </ALink>
            </div>
        </div>
    );

    /** Render */
    return (
        <>
            <PaymentLinkCard 
                title={t('PAYMENT_CONFIRMATION_TITLE', props.language)}
                className="payment-link-confirm-container"
            >
                {isLoading && cmpChecking}
                {!isLoading && !paymentLink && cmpNotFound}
                {!isLoading && paymentLink && finalStatus === PaymentLinkStatus.PAID && cmpPaymentSuccess}
                {!isLoading && paymentLink && finalStatus === PaymentLinkStatus.PAYMENT_FAILED && cmpPaymentFailed}
                {!isLoading && paymentLink && (finalStatus === PaymentLinkStatus.PAYMENT_PENDING || finalStatus === PaymentLinkStatus.CREATED) && cmpPaymentPending}
            </PaymentLinkCard>
            
            <PaymentContactSupport language={props.language} />
        </>
    );
}

export default PaymentLinkConfirmPayment;